/**
 *  Component: Link Team with Channel
 */
import React, { useState, useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Grid } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { InfoWorkZero } from '@mesh.ai/rabbit';
import TeamChannelMapping from 'models/admin/teamChannelMapping';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { StyledTypography, StyledTooltip } from 'styles/common';

import { Plus } from 'assets/assetsV1/iconComponents';
import emptyStateImage from 'assets/images/emptyState.png';

import { SEARCH_TAG_TYPES, SLACK_PRIVATE_CHANNEL_INFO_TEXT } from 'constants/ProjectConstants';
import theme from 'constants/theme';

import {
  getChannels,
  getTeamChannelMappings,
  saveTeamChannelMapping,
  updateTeamChannelMapping,
  deleteTeamChannelMapping,
} from 'services/plugins';
import { universalSearchAutoCompleteV1 } from 'services/search';
import { showErrorSnackbar } from 'services/snackbar';

import { Button } from 'uiToolkitV1/Button';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TextLabel } from 'uiToolkitV1/Typography';

import { StepCard } from '../commonComponents';
import SlackMappingFields, {
  OptionsContainerWithPrefixChannelIcon,
  ValueContainerWithPrefixChannelIcon,
} from './slackMappingFields';

const thirdPartyType = 'slack';

const Image = styled.img`
  cursor: pointer;
  margin: 0 auto;
  display: table;
`;

const SlackPlugin = ({ isActivated = false }) => {
  const dispatch = useDispatch();
  const [dataLoading, setDataLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [newMapping, setNewMapping] = useState(null);
  const [teams, setTeams] = useState([]);

  const getMappings = () => {
    setDataLoading(true);
    getTeamChannelMappings({ thirdPartyType })
      .then((mappings1) => {
        setMappings(mappings1);
      })
      .catch(() => dispatch(showErrorSnackbar('Sorry, unable to fetch the mappings')))
      .finally(() => setDataLoading(false));
  };

  const getAllChannels = () => {
    setDataLoading(true);
    getChannels({ thirdPartyType })
      .then((res) => {
        if (res.length) {
          setChannels(res.map((channel) => ({ value: channel.id, label: channel.name })));
          getMappings();
        } else {
          dispatch(showErrorSnackbar('No channels were found'));
          setDataLoading(false);
        }
      })
      .catch(() => {
        dispatch(showErrorSnackbar('Sorry, unable to fetch the channels!'));
        setDataLoading(false);
      });
  };

  const searchCallBack = (res) => {
    const teamsResult = res ? res.filter((r) => r.label === SEARCH_TAG_TYPES.team) : [];
    setDataLoading(false);
    if (teamsResult.length) {
      if (isActivated) {
        getAllChannels();
      }
      setTeams(teamsResult[0].options.map((r) => ({ label: r.label, value: r.id })));
    } else {
      dispatch(showErrorSnackbar('No teams were found'));
    }
  };

  const getTeams = () => {
    const tagsSet = [
      {
        tagType: 'TEAM',
        limit: 50,
      },
    ];
    universalSearchAutoCompleteV1({ text: '', tagsSet, callback: searchCallBack });
  };

  useEffect(() => {
    setDataLoading(true);
    getTeams();
  }, []);

  const getThirdPartyTeamName = (id) => channels.filter((channel) => channel.value === id)[0].label;

  const createMapping = (value, key) => {
    if (key === 'team') {
      if (newMapping.channel) {
        setLoading(true);
        saveTeamChannelMapping({
          thirdPartyType,
          thirdPartyTeamId: newMapping.thirdPartyTeamId,
          thirdPartyTeamName: getThirdPartyTeamName(newMapping.thirdPartyTeamId),
          teamId: value,
        })
          .then(() => {
            getMappings();
          })
          .catch(() => dispatch(showErrorSnackbar('Sorry, unable to save the mapping!')))
          .finally(() => {
            setNewMapping(null);
            setLoading(false);
          });
      } else {
        setNewMapping({ ...newMapping, teamId: value });
      }
    } else if (newMapping.teamId) {
      setLoading(true);
      saveTeamChannelMapping({
        thirdPartyType,
        thirdPartyTeamId: value,
        thirdPartyTeamName: getThirdPartyTeamName(value),
        teamId: newMapping.teamId,
      })
        .then(() => {
          getMappings();
        })
        .catch(() => dispatch(showErrorSnackbar('Sorry, unable to save the mapping!')))
        .finally(() => {
          setNewMapping(null);
          setLoading(false);
        });
    } else {
      setNewMapping({ ...newMapping, thirdPartyTeamId: value });
    }
  };

  const updateMapping = (value, key, mapping) => {
    if (key === 'team') {
      setLoading(true);
      updateTeamChannelMapping({
        ...mapping,
        thirdPartyType,
        teamId: value,
      })
        .then(() => {
          getMappings();
        })
        .catch(() => {
          dispatch(showErrorSnackbar('Sorry, unable to update the mapping!'));
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      updateTeamChannelMapping({
        ...mapping,
        thirdPartyType,
        thirdPartyTeamId: value,
        thirdPartyTeamName: getThirdPartyTeamName(value),
      })
        .then(() => {
          getMappings();
        })
        .catch(() => {
          dispatch(showErrorSnackbar('Sorry, unable to update the mapping!'));
          setLoading(false);
        })
        .finally(() => setLoading(false));
    }
  };

  const onButtonClick = () => setNewMapping(new TeamChannelMapping());

  const deleteMapping = (mappingId) => {
    setLoading(true);
    deleteTeamChannelMapping({ thirdPartyType, mappingId })
      .then(() => {
        getMappings();
      })
      .catch(() => {
        dispatch(showErrorSnackbar('Sorry, unable to delete the mapping!'));
      })
      .finally(() => setLoading(false));
  };

  const removeNewMapping = () => setNewMapping(null);

  return (
    <StepCard
      title="Link slack channels for pod updates"
      headerRightComp={
        <Button StartIcon={Plus} onClick={onButtonClick} isDisabled={loading || newMapping} variant="outlined">
          Link new Slack channels
        </Button>
      }
    >
      {(!mappings || !mappings.length) && !newMapping && !loading && !dataLoading ? (
        <>
          <Image alt="link" src={emptyStateImage} onClick={onButtonClick} />
          <StyledTypography weight={600} variant="h5" align="center" color={theme.palette.text.disabled}>
            Click Above to Add more Channels
          </StyledTypography>
        </>
      ) : (
        <Box>
          <Grid container alignItems="center">
            <Grid item xs={5}>
              <TextLabel color="text.g2">Team name</TextLabel>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={5}>
              <Box display="flex" alignItems="center" j>
                <TextLabel color="text.g2">Slack channel</TextLabel>

                <StyledTooltip title={SLACK_PRIVATE_CHANNEL_INFO_TEXT}>
                  <div>
                    <InfoWorkZero fontSize="24px" color="#666" />
                  </div>
                </StyledTooltip>
              </Box>
            </Grid>
          </Grid>

          <Box>
            {dataLoading ? (
              <Grid container>
                <Grid item xs={5}>
                  <Skeleton height="30px" />
                  <Skeleton height="30px" />
                </Grid>
                <Grid item xs={1} />
                <Grid item xs={5}>
                  <Skeleton height="30px" width="80%" />
                  <Skeleton height="30px" width="80%" />
                </Grid>
              </Grid>
            ) : (
              <Flex flexDir="column" gap="12px">
                {mappings.map((mapping) => (
                  <SlackMappingFields
                    loading={loading}
                    saveFn={updateMapping}
                    deleteFn={deleteMapping}
                    teams={teams}
                    channels={channels}
                    mapping={mapping}
                  />
                ))}
              </Flex>
            )}

            {
              // new
              newMapping && (
                <SlackMappingFields
                  loading={loading}
                  saveFn={createMapping}
                  deleteFn={removeNewMapping}
                  teams={teams}
                  channels={channels}
                  mapping={newMapping}
                />
              )
            }
          </Box>
        </Box>
      )}
    </StepCard>
  );
};

export const ApplauseContainer = ({ saveFn, updateFn, channels, value }) => (
  <>
    <TextLabel color="text.g2" mb={1}>
      Feedback Channel
    </TextLabel>
    <Grid container alignItems="center" justifyContent="start">
      <Grid item xs={5} style={{ marginTop: '8px' }}>
        <SelectDashed
          value={value ?? null}
          handleChange={(data) => {
            if (saveFn) {
              saveFn(data.value, data.label);
              return;
            }
            updateFn(
              data.value,
              data.label,
              value.map((res) => res.uuid)
            );
          }}
          options={channels}
          components={{
            SingleValue: ValueContainerWithPrefixChannelIcon,
            Option: OptionsContainerWithPrefixChannelIcon,
          }}
        />
      </Grid>
    </Grid>
  </>
);

export default SlackPlugin;
